export function getLocalStorageValue<T extends string | number | boolean>(
  key: string,
  defaultValue: T
): T {
  try {
    // Check if localStorage is available (not in SSR/Node environment)
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('localStorage is not available. Using default value.');
      return defaultValue;
    }

    const storedValue = localStorage.getItem(key);

    // If item doesn't exist or is null, return default
    if (storedValue === null || storedValue === '') {
      return defaultValue;
    }

    // Determine the type based on the default value
    const targetType = typeof defaultValue;

    switch (targetType) {
      case 'string':
        return storedValue as T;

      case 'number': {
        // Check if it's a valid number
        const trimmedValue = storedValue.trim();
        // from Copilot: this regex did not consider scientific notation.
        // Which should be a minor issue.
        if (!/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
          console.warn(
            `Invalid number format in localStorage for key ${key}: "${storedValue}". Using default value.`
          );
          return defaultValue;
        }

        const numValue = Number(trimmedValue);

        // Check for NaN or Infinity
        if (isNaN(numValue) || !isFinite(numValue)) {
          console.warn(
            `Invalid number value in localStorage for key ${key}: "${storedValue}". Using default value.`
          );
          return defaultValue;
        }

        return numValue as T;
      }

      case 'boolean': {
        // Handle common boolean representations
        const lowerValue = storedValue.toLowerCase().trim();

        if (['true', '1', 'yes', 'on'].includes(lowerValue)) {
          return true as T;
        }

        if (['false', '0', 'no', 'off'].includes(lowerValue)) {
          return false as T;
        }

        console.warn(
          `Invalid boolean value in localStorage for key ${key}: "${storedValue}". Using default value.`
        );
        return defaultValue;
      }

      default:
        console.warn(`Unsupported type for localStorage key ${key}. Using default value.`);
        return defaultValue;
    }
  } catch (error) {
    // Handle any localStorage access errors (e.g., SecurityError, QuotaExceededError)
    console.error(`Error reading from localStorage for key ${key}:`, error);
    return defaultValue;
  }
}

// Companion function to safely set values in localStorage
export function setLocalStorageValue<T extends string | number | boolean>(
  key: string,
  value: T
): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('localStorage is not available.');
      return false;
    }

    localStorage.setItem(key, String(value));
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage for key ${key}:`, error);
    return false;
  }
}

// Companion function to remove values from localStorage
export function removeLocalStorageValue(key: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('localStorage is not available.');
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage for key ${key}:`, error);
    return false;
  }
}

// read write obj to localStorage START
export function getLocalStorageJSON<T>(key: string, defaultValue: T): T {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return defaultValue;
    }

    const storedValue = localStorage.getItem(key);

    if (storedValue === null || storedValue === '') {
      return defaultValue;
    }

    // Try to parse as JSON
    const parsed = JSON.parse(storedValue);

    // Basic type validation - you might want to add more sophisticated validation
    if (typeof parsed !== typeof defaultValue) {
      console.warn(`Type mismatch in localStorage for key ${key}. Using default value.`);
      return defaultValue;
    }

    return parsed as T;
  } catch (error) {
    console.error(`Error parsing JSON from localStorage for key ${key}:`, error);
    return defaultValue;
  }
}

export function setLocalStorageJSON<T>(key: string, value: T): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }

    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error stringifying JSON to localStorage for key ${key}:`, error);
    return false;
  }
}

export const KEY_USERNAME = 'username';
export const KEY_JWT = 'jwt-admin';
export const KEY_REFRESH_TOKEN = 'refreshToken-admin';
